import { HttpClient } from '@angular/common/http';
import { DestroyRef, Injectable, Injector, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService, isConnectivityFailure } from '@lib/auth/data-access';
import { FeatureModel, FeatureTenantModel, FeatureToggleType } from '@lib/feature-toggle/models';
import { EnvironmentService } from '@lib/shared/data-access';
import { catchError, exhaustMap, filter, firstValueFrom, from, fromEvent, merge, Observable, of, Subscription, take, tap, timer } from 'rxjs';
import { OFFLINE_FEATURE_TOGGLE_STORE } from './offline-feature-toggle-store.token';

@Injectable({
    providedIn: 'root'
})
export class FeatureToggleService {

    // signals
    private tenantFeaturesSourceSignal = signal<FeatureTenantModel[]>([]);
    public tenantFeaturesComputedSignal = computed(() => { return this.tenantFeaturesSourceSignal() });

    /**
     * Whether the toggle list has been fetched yet — SETTLED, not successful.
     *
     * `tenantFeaturesComputedSignal()` is `[]` both before the fetch and when the tenant genuinely has no
     * features enabled, and nothing could tell those two apart. That ambiguity is harmless for a menu item
     * (it appears a moment late) and is not harmless for a route decision: a guard reading "no features" on a
     * cold deep link would send an officer to the legacy stack while V2 was on for their tenant, and the
     * answer would then be wrong for the whole navigation rather than late.
     *
     * SET ON THE ERROR PATH TOO. A guard that waits for this must not wait forever because the auth API is
     * down — the empty list IS the answer then, and it means "no features", which is the safe reading.
     */
    private tenantFeaturesLoadedSourceSignal = signal(false);
    public tenantFeaturesLoadedComputedSignal = this.tenantFeaturesLoadedSourceSignal.asReadonly();

    /**
     * Whether the last fetch FAILED, so the empty list means "nothing answered" rather than "no features".
     *
     * Loaded-and-empty is the safe reading for a route or a menu item. It is not for something that HIDES WORK:
     * the V2 charging rail hides a section when its feature is off, and reading a failed fetch as "off" would take
     * Bill of Costs away from a tenant that has it, and turn an officer out of the section they were in, for as
     * long as the auth API is unreachable.
     */
    private tenantFeaturesFailedSourceSignal = signal(false);
    public tenantFeaturesFailedComputedSignal = this.tenantFeaturesFailedSourceSignal.asReadonly();

    private httpClient = inject(HttpClient);
    private environmentService = inject(EnvironmentService);
    private destroyRef = inject(DestroyRef);
    public authService = inject(AuthService);

    // Add your service methods here
    public getFeatures(): Observable<FeatureModel[]> {
        return this.httpClient.get<FeatureModel[]>(`${this.environmentService.authApiUrl}/api/v1/feature-toggles`);
    }

    public saveFeature(feature: FeatureModel): Observable<FeatureModel> {
        return this.httpClient.put<FeatureModel>(`${this.environmentService.authApiUrl}/api/v1/feature-toggles/${feature.id}`, feature);
    }

    public saveTenantFeature(tenantFeature: FeatureTenantModel): Observable<FeatureTenantModel> {
        return this.httpClient.put<FeatureTenantModel>(`${this.environmentService.authApiUrl}/api/v1/feature-toggles/tenants/${tenantFeature.tenantId}/tenant-feature/${tenantFeature.id}`, tenantFeature);
    }   

    public getTenantFeatures(tenantExternalId: string): Observable<FeatureTenantModel[]> {
        return this.httpClient.get<FeatureTenantModel[]>(`${this.environmentService.authApiUrl}/api/v1/feature-toggles/t/${tenantExternalId}`);
    }

    public isFeatureEnabledServerCheck(featureToggleType: FeatureToggleType): Observable<boolean> {
        return this.httpClient.get<boolean>(`${this.environmentService.authApiUrl}/api/v1/feature-toggles/t/${this.authService.userPrincipalComputedSignal()?.currentTenantExternalId}/type/${featureToggleType}/status`);
    }

    public getLoggedUserEnabledFeatures(tenantExternalId: string): Observable<FeatureTenantModel[]> {
        if (window.location.href.includes('/super-admin') && this.authService.isCodyAdministratorComputedSignal()) {
            // A settled answer, so anything waiting on the load is released rather than left hanging on a
            // request that is never going to be made.
            this.tenantFeaturesLoadedSourceSignal.set(true);
            return of([]);
        }
        return this.fetchLoggedUserEnabledFeatures(tenantExternalId).pipe(
            tap((tenantFeatures) => {
                this.applyLoadedFeatures(tenantFeatures);
                void this.saveFeaturesOffline(tenantExternalId, tenantFeatures);
            }),
            catchError((error: unknown) => {
                // NO CONNECTION: the device's last good list, so an offline reload still knows the tenant's
                // features — see `recoverFeaturesOffline`. A server that ANSWERED with a failure is handled as it
                // always was, below.
                if (isConnectivityFailure(error)) {
                    return from(this.recoverFeaturesOffline(tenantExternalId));
                }
                return of(this.applyFailedLoad());
            })
        );
    }

    /**
     * Whether the toggle list in use was RESTORED from this device's offline copy because the fetch could not reach
     * the server. Cleared by the first successful fetch, which is retried on its own once the connection is back.
     */
    private tenantFeaturesRestoredOfflineSourceSignal = signal(false);
    public tenantFeaturesRestoredOfflineComputedSignal = this.tenantFeaturesRestoredOfflineSourceSignal.asReadonly();

    /** For the offline copy, read lazily — see OFFLINE_FEATURE_TOGGLE_STORE. */
    private injector = inject(Injector);

    /** Waiting for the connection, to replace a restored list with the server's. */
    private reconnectRefresh: Subscription | null = null;

    private fetchLoggedUserEnabledFeatures(tenantExternalId: string): Observable<FeatureTenantModel[]> {
        return this.httpClient.get<FeatureTenantModel[]>(`${this.environmentService.authApiUrl}/api/v1/feature-toggles/t/${tenantExternalId}/logged-user`);
    }

    private applyLoadedFeatures(tenantFeatures: FeatureTenantModel[]): void {
        this.tenantFeaturesSourceSignal.set(tenantFeatures);
        this.tenantFeaturesFailedSourceSignal.set(false);
        this.tenantFeaturesRestoredOfflineSourceSignal.set(false);
        this.tenantFeaturesLoadedSourceSignal.set(true);
    }

    /** The failure path as it has always been: an empty list, marked failed, and settled. */
    private applyFailedLoad(): FeatureTenantModel[] {
        // Handle the error by setting an empty array to indicate no data or an error state
        this.tenantFeaturesSourceSignal.set([]);
        this.tenantFeaturesFailedSourceSignal.set(true);
        // Settled, not successful — see tenantFeaturesLoadedComputedSignal.
        this.tenantFeaturesLoadedSourceSignal.set(true);
        // Return an empty array to continue the observable chain
        return [];
    }

    /**
     * A fetch that could not reach the server: keep what is known, else restore the device's copy, else fail as before.
     *
     * KEEP FIRST. A list already loaded on this page (from the server, or restored) is at least as fresh as the
     * device's copy, and wiping it because a LATER fetch had no connection would switch the V2 stack off under an
     * officer who was using it.
     *
     * A restored list is presented exactly as a loaded one — loaded, not failed — because that is what it is for:
     * every reader of these signals (the V2 gate, the route switch, the menu) behaves as it did when the list
     * was last fetched.
     *
     * BOTH ONLY FOR A TENANT ON THE V2 STACK. The store only offers a copy for the tenant and user it was saved for,
     * and only when that copy has Pathfinder V2 on; the keep is held to the same rule. A V2-off tenant's legacy app
     * therefore fails a fetch exactly as it always has.
     */
    private async recoverFeaturesOffline(tenantExternalId: string): Promise<FeatureTenantModel[]> {
        const current = this.tenantFeaturesSourceSignal();
        if (this.tenantFeaturesLoadedSourceSignal() && !this.tenantFeaturesFailedSourceSignal()) {
            // Already known on this page: kept for a V2 tenant, and for any other the failure path it always took,
            // without so much as opening the offline database.
            return current.some((feature) => feature.id === FeatureToggleType.PathfinderV2 && feature.isEnabled)
                ? current
                : this.applyFailedLoad();
        }

        const store = this.injector.get(OFFLINE_FEATURE_TOGGLE_STORE, null);
        let restored: FeatureTenantModel[] | null = null;
        if (store) {
            try {
                restored = await FeatureToggleService.withinLimit(store.restore(tenantExternalId));
            } catch (error) {
                console.warn('Could not read the offline feature toggles.', error);
            }
        }

        if (!restored) {
            return this.applyFailedLoad();
        }

        this.tenantFeaturesSourceSignal.set(restored);
        this.tenantFeaturesFailedSourceSignal.set(false);
        this.tenantFeaturesRestoredOfflineSourceSignal.set(true);
        this.tenantFeaturesLoadedSourceSignal.set(true);
        console.info('No connection: the feature toggles were restored from this device.');
        this.refreshWhenOnline(tenantExternalId);
        return restored;
    }

    /** Keep a fetched list as this device's offline fallback. Best-effort; a failed write changes nothing online. */
    private async saveFeaturesOffline(tenantExternalId: string, tenantFeatures: FeatureTenantModel[]): Promise<void> {
        const store = this.injector.get(OFFLINE_FEATURE_TOGGLE_STORE, null);
        if (!store) {
            return;
        }
        try {
            await store.save(tenantExternalId, tenantFeatures);
        } catch (error) {
            console.warn('Could not keep the feature toggles for offline use.', error);
        }
    }

    /**
     * Longest a fetch failure waits for the device's copy. The copy is read from the offline database, whose first
     * open on a page can be held up (another window still on an older schema); a guard waiting on this must never
     * become a blank screen, so past this the copy counts as absent.
     */
    private static readonly RESTORE_LIMIT_MS = 2_000;

    /** How often a restored list is re-fetched while the device believes it is online. */
    private static readonly REFRESH_RETRY_MS = 30_000;

    /** The restore, or null once {@link RESTORE_LIMIT_MS} has passed. */
    private static withinLimit<T>(restore: Promise<T | null>): Promise<T | null> {
        let timer: ReturnType<typeof setTimeout> | undefined;
        const limit = new Promise<null>((resolve) => {
            timer = setTimeout(() => resolve(null), FeatureToggleService.RESTORE_LIMIT_MS);
        });
        return Promise.race([restore, limit]).finally(() => clearTimeout(timer));
    }

    /**
     * Replace a restored list with the server's once the connection is back, so a toggle changed while this device
     * was offline takes effect without a reload. A fetch that still fails leaves the restored list in place — it
     * never falls through to the empty-list failure path, which would switch features off mid-session.
     *
     * On the `online` event AND on a timer while the device believes it is online: a restore that happened with
     * `navigator.onLine` still true (a server or VPN that could not be reached) gets no `online` event at all, and
     * the one fetch made at the instant of `online` often loses the race with DNS or the VPN coming back.
     */
    private refreshWhenOnline(tenantExternalId: string): void {
        if (this.reconnectRefresh || typeof window === 'undefined') {
            return;
        }

        this.reconnectRefresh = merge(fromEvent(window, 'online'), timer(FeatureToggleService.REFRESH_RETRY_MS, FeatureToggleService.REFRESH_RETRY_MS))
            .pipe(
                filter(() => navigator.onLine !== false),
                exhaustMap(() => this.fetchLoggedUserEnabledFeatures(tenantExternalId).pipe(catchError(() => of(null)))),
                filter((tenantFeatures): tenantFeatures is FeatureTenantModel[] => Array.isArray(tenantFeatures)),
                take(1),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((tenantFeatures) => {
                this.reconnectRefresh = null;
                this.applyLoadedFeatures(tenantFeatures);
                void this.saveFeaturesOffline(tenantExternalId, tenantFeatures);
            });
    }

    /** One in-flight load, so simultaneous callers share a request rather than each starting one. */
    private inFlightLoad: Promise<void> | null = null;

    /**
     * Make sure the tenant's toggles are loaded, fetching them if nobody has.
     *
     * WHY A CALLER MIGHT HAVE TO ASK. The only thing that loaded these was `AppMenuComponent`, which lives
     * inside the app shell — so anything that runs BEFORE the shell exists could not simply wait for them.
     * A route guard is exactly that: it decides whether a route matches, which happens before any component
     * of that route tree is created. Waiting there deadlocked outright — the guard waited for the toggles,
     * and the toggles waited for a component the guard was blocking — and a hard refresh on a gated URL
     * rendered a blank page while back and forward worked, because those reused a shell that already existed.
     *
     * Idempotent and shared: already loaded resolves immediately, and concurrent callers get the same
     * promise. It never rejects — `getLoggedUserEnabledFeatures` swallows its own failure into an empty list,
     * which is the safe reading and keeps a caller from hanging on a down auth API.
     */
    public ensureTenantFeaturesLoaded(tenantExternalId: string): Promise<void> {
        if (this.tenantFeaturesLoadedSourceSignal()) {
            return Promise.resolve();
        }

        this.inFlightLoad ??= firstValueFrom(this.getLoggedUserEnabledFeatures(tenantExternalId))
            .then(() => undefined)
            .finally(() => { this.inFlightLoad = null; });

        return this.inFlightLoad;
    }

    public isFeatureEnabled(featureType: FeatureToggleType): boolean {
        return this.tenantFeaturesComputedSignal().some(feature => feature.id === featureType && feature.isEnabled);
    }
}
